import { addDays, addMonths, addQuarters, addWeeks, addYears, differenceInCalendarDays, endOfMonth, startOfMonth, format, setMonth, startOfWeek, subDays, parseISO, isValid } from 'date-fns'
import codeUtil from "@plannel/pages/utils/code-util";
import { isNil } from 'lodash';

export const DEFAULT_DATE_FORMAT = "yyyy-MM-dd";
export const ISO_8601_REGEX = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+/;
export const DATETIME_FORMAT = "yyyy-MM-dd HH:mm:ss";
/*
 * 초를 뗀 시각. 사람이 "언제 썼는지"만 알면 되는 자리(노트 스레드 등)에 쓴다 —
 * 그리드 감사 컬럼처럼 정렬·대조가 목적인 곳은 초까지 있는 DATETIME_FORMAT 을 그대로 쓸 것.
 */
export const DATETIME_MINUTE_FORMAT = "yyyy-MM-dd HH:mm";

const buckets = codeUtil.buckets;

const dateValidation = (date) => {
  return Object.prototype.toString.call(date) === "[object Date]" && !(isNaN(date));
};

const formatDateTime = (data) => {
  if (!data) {
    return "";
  }

  const isObject = (data instanceof Object) && !(data instanceof Date);
  const value = isObject ? data.value : data;

  if (isNil(value)) {
    return "";
  }

  const _date = new ZDate(value);

  return dateValidation(_date) ? format(_date, DATETIME_FORMAT) : "";
};

const formatDate = (dateStr, formatStr) => {
  if (isNil(dateStr)) {
    return "";
  }

  const _date = new ZDate(dateStr);
  return dateValidation(_date) ? format(_date, formatStr) : "";
};

const endOfMonthDate = (date) => {
  return dateValidation(date) ? endOfMonth(date) : "";
};

const startOfMonthDate = (date) => {
  return dateValidation(date) ? startOfMonth(date) : "";
};

const setMonthDate = (date, month) => {
  return dateValidation(date) ? setMonth(date, month) : "";
};

const getMyDayId = (dayCd) => {
  const dayOfWeek = codeUtil.dayOfWeek;
  if (!dayCd || dayCd === "") {
    return dayOfWeek.MONDAY.id;
  }
  for (const day in dayOfWeek) {
    if (dayOfWeek[day].cd === dayCd) {
      return dayOfWeek[day].id;
    }
  }
  return dayOfWeek.MONDAY.id;
};

const nearDay = (target, dayOfWeek, timeDimension) => {
  let gap = 0;
  if (timeDimension !== null) {
    if (timeDimension === buckets.PARTIAL_WEEK) {
      const dayGap = getMyDayId(dayOfWeek) - target.getDay();
      const monthStartGap = target.getDate() - 1;
      gap = Math.abs(dayGap) < Math.abs(monthStartGap) ? dayGap : monthStartGap;
    } else if (timeDimension === buckets.WEEK) {
      gap = getMyDayId(dayOfWeek) - target.getDay();
    }
  }
  return addDays(target, gap);
};

const finishedNearDay = (target, dayOfWeek) => {
  const gap = getMyDayId(dayOfWeek) - target.getDay() - 1;

  const now = new ZDate();
  now.setHours(0, 0, 0, 0);

  const nearDay = addDays(target, gap);
  if (nearDay > now) {
    return addDays(target, gap - 7);
  }

  return addDays(target, gap);
};

const getWeekOfYear = (date) => {
  const currentDate = new ZDate(date);
  const firstDayOfYear = new ZDate(currentDate.getFullYear(), 0, 1);
  const pastDaysOfYear = (currentDate - firstDayOfYear) / (24 * 60 * 60 * 1000);
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}

const getDayOfWeek = (day) => {
  switch (day.toLowerCase()) {
    case codeUtil.dayOfWeek.MONDAY.cd: return 1;
    case codeUtil.dayOfWeek.TUESDAY.cd: return 2;
    case codeUtil.dayOfWeek.WEDNESDAY.cd: return 3;
    case codeUtil.dayOfWeek.THURSDAY.cd: return 4;
    case codeUtil.dayOfWeek.FRIDAY.cd: return 5;
    case codeUtil.dayOfWeek.SATURDAY.cd: return 6;
    case codeUtil.dayOfWeek.SUNDAY.cd: return 0;
    default: return 1;
  }
};

const getWeekString = (date) => {
  const myWeek = getWeekOfYear(date);
  let month = date.getMonth() + 1;
  if (date.getMonth() < 10) {
    month = `0${month}`
  }
  let day = date.getDate()
  if (date.getDate() < 10) {
    day = `0${day}`
  }
  if (myWeek < 10) {
    return `${month}/${day} W0${myWeek}`;
  } else {
    return `${month}/${day} W${myWeek}`;
  }
}

//DP header
const headerDateFormatter = (date, timeDimension) => {
  date = new ZDate(date);
  let dateFormatted = `${date.getFullYear()}-${date.getMonth() + 1 < 10 ? `0${(date.getMonth() + 1)}` : date.getMonth() + 1}`;
  if (codeUtil.isWeekBucket(timeDimension)) {
    dateFormatted = `${dateFormatted}-${date.getDate() < 10 ? `0${(date.getDate())}` : date.getDate()}`;
  } else if (timeDimension === buckets.QUARTER) {
    if (date.getMonth() === 0) {
      dateFormatted = `${date.getFullYear()}-1Q`;
    } else if (date.getMonth() === 3) {
      dateFormatted = `${date.getFullYear()}-2Q`;
    } else if (date.getMonth() === 6) {
      dateFormatted = `${date.getFullYear()}-3Q`;
    } else {
      dateFormatted = `${date.getFullYear()}-4Q`;
    }
  } else if (timeDimension === buckets.YEAR) {
    dateFormatted = `${date.getFullYear()}`;
  } else if (timeDimension === buckets.DAY) {
    const month = date.getMonth() + 1 < 10 ? `0${(date.getMonth() + 1)}` : date.getMonth() + 1;
    const day = date.getDate() < 10 ? `0${(date.getDate())}` : date.getDate();
    dateFormatted = `${date.getFullYear()}-${month}-${day}`;
  }
  return dateFormatted;
};


const calculateRangeDate = (targetDate, bucket, startDay, range, isEndDate) => {
  let target = new ZDate(targetDate);
  if (range === 0) {
    return target;
  }

  let index = 0;
  switch (bucket) {
    case codeUtil.buckets.DAY:
      target = addDays(target, range);
      break;
    case codeUtil.buckets.WEEK:
      target = addWeeks(target, range);
      break;
    case codeUtil.buckets.PARTIAL_WEEK:
      if (!startDay) {
        startDay = 'mon';
      }
      if (range > 0) {
        while (index < range) {
          const nextTarget = startOfWeek(addDays(target, 7), { weekStartsOn: getDayOfWeek(startDay) });
          const firstOfNextMonth = startOfMonth(addMonths(target, 1));
          if (firstOfNextMonth < nextTarget) {
            target = firstOfNextMonth;
          } else {
            target = nextTarget;
          }
          index++;
        }
      } else {
        let crossFirstOfMonth = false;
        while (index > range) {
          const previous = startOfWeek(subDays(target, 7), { weekStartsOn: getDayOfWeek(startDay) });
          const firstOfCurrentMonth = startOfMonth(target);
          if (crossFirstOfMonth) {
            target = startOfWeek(target, { weekStartsOn: getDayOfWeek(startDay) });
            crossFirstOfMonth = false;
          } else if (target > firstOfCurrentMonth && previous < firstOfCurrentMonth) {
            target = firstOfCurrentMonth;
            crossFirstOfMonth = true;
          } else if (target === firstOfCurrentMonth) {
            target = subDays(firstOfCurrentMonth, 1);
          } else {
            target = previous;
          }
          index--;
        }
      }
      break;
    case codeUtil.buckets.MONTH:
      target = addMonths(startOfMonth(target), range);
      break;
    case codeUtil.buckets.QUARTER:
      target = addQuarters(startOfMonth(target), range);
      break;
    default:
      target = addYears(startOfMonth(target), range);
  }

  if (isEndDate) {
    target = subDays(target, 1);
  }

  return new ZDate(target.getFullYear(), target.getMonth(), target.getDate(), 0, 0, 0);
};

/*
 * date 를 시작일로 하는 버킷(WEEK/MONTH/QUARTER) 하나가 실제로 며칠에 걸쳐있는지(day-span) 반환한다.
 * calculateRangeDate(date, bucket, startDay, 1, true) 로 그 버킷의 마지막 날을 구해 차이를 낸다 —
 * MONTH 는 그 달의 실제 일수(28~31), WEEK 는 7일, QUARTER 는 그 분기 3개월의 실제 합산 일수(89~92)가 나온다.
 * 백엔드 DateUtil.daysInBucket 과 동일한 규칙.
 */
const daysInBucket = (date, bucket, startDay) => {
  if (!date) {
    return 30;
  }
  const end = calculateRangeDate(date, bucket, startDay, 1, true);
  return differenceInCalendarDays(end, date) + 1;
};

/*
 * date 가 속한 버킷의 시작일. 백엔드 DateUtil.startDateOfBucket 과 같은 규칙이다.
 *   WEEK         : 그 날짜 이전(또는 같은) 주 시작 요일.  예) 2026-10-25(일), 월요일 시작 → 2026-10-19
 *   PARTIAL_WEEK : 위와 같되 주 시작이 이전 달이면 그 달 1일
 *   그 밖의 버킷  : 날짜를 그대로 돌려준다(월 이상은 호출부가 1일로 맞춰 쓴다)
 * calculateRangeDate(date, bucket, startDay, 0, …) 는 range 0 이면 날짜를 그대로 돌려주므로 시작일 계산용이 아니다.
 */
const startDateOfBucket = (date, bucket, startDay) => {
  const target = new ZDate(date);
  if (!codeUtil.isWeekBucket(bucket)) {
    return target;
  }
  const weekStart = startOfWeek(target, { weekStartsOn: getDayOfWeek(startDay || codeUtil.dayOfWeek.MONDAY.cd) });
  if (bucket === buckets.PARTIAL_WEEK) {
    const monthStart = startOfMonth(target);
    return new ZDate(weekStart < monthStart ? monthStart : weekStart);
  }
  return new ZDate(weekStart);
};

const makeTimeBucket = (start, end, bucketUnit, startDay) => {
  const bucket = [];
  let day = startDay;
  let startDate = start;
  if (codeUtil.isWeekBucket(bucketUnit)) {
    if (startDay !== null) {
      day = codeUtil.dayOfWeek.MONDAY.cd;
    }
    const isFirstDay = bucketUnit === codeUtil.buckets.PARTIAL_WEEK && start.getDate() === 1;
    startDate = isFirstDay ? start : nearDay(start, day, bucketUnit);
  }
  if (end !== null) {
    let headerDate = new ZDate(startDate);
    let index = 0;
    while (headerDate <= new ZDate(end)) {
      headerDate = calculateRangeDate(startDate, bucketUnit, startDay, index, false);
      if (!codeUtil.isWeekBucket(bucketUnit)) {
        headerDate = new ZDate(headerDate.getFullYear(), headerDate.getMonth(), 1);
      }
      if (headerDate > new ZDate(end)) {
        break;
      }
      bucket.push(headerDateFormatter(headerDate, bucketUnit));
      index++;
    }
  }
  return bucket;
}

const isBetween = (date, min, max) => (date.getTime() >= min.getTime() && date.getTime() <= max.getTime());

const getDateWithoutTime = dateTime => {
  const _date = new ZDate(dateTime);
  return new ZDate(_date.getFullYear(), _date.getMonth(), _date.getDate());
}

const getToday = () => {
  const date = new ZDate();
  const year = date.getFullYear();
  const month = (`0${(1 + date.getMonth())}`).slice(-2);
  const day = (`0${date.getDate()}`).slice(-2);
  return year + month + day;
}

const toTimeString = (totalSeconds) => {
  const secondValue = totalSeconds?.value ?? totalSeconds;
  const totalMs = secondValue * 1000;
  return new ZDate(totalMs).toISOString().slice(11, 19);
}

    /*ex)
    * getStartDayByRange(start day : mon, range = -1 ) -> sunday
    * getStartDayByRange(start day : sun, range = 5 ) -> friday
    */
const getStartDayByRange = (startDay, range) => {
  const intToDay = {};
  const dayToInt = {};
  const dayOfWeek = codeUtil.dayOfWeek;
  for (const day of Object.keys(dayOfWeek)) {
    intToDay[dayOfWeek[day].id] = dayOfWeek[day].cd;
    dayToInt[dayOfWeek[day].cd] = dayOfWeek[day].id;
  }

  const value = dayToInt[startDay];
  let computeVal = value + range;
  if (computeVal > 0) {
    computeVal = (value + range) % 7;
  } else {
    computeVal = (Math.abs(value + range) % 7);
    if (computeVal > 0) {
      computeVal = 7 - computeVal;
    }
  }
  return intToDay[computeVal];
}

const isDateString = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") {
    return false;
  }

  const trimmedDateStr = dateStr.trim();
  if (trimmedDateStr.length === 0) {
    return false;
  }
  const isYearMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(trimmedDateStr);
  const isYearMonthDay = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(trimmedDateStr);

  if (!isYearMonth && !isYearMonthDay) {
    return false;
  }

  const date = parseISO(isYearMonth ? `${trimmedDateStr}-01` : trimmedDateStr);
  return isValid(date);
};

const dateUtils = {
  dateValidation,
  formatDateTime,
  formatDate,
  endOfMonthDate,
  startOfMonthDate,
  setMonthDate,
  headerDateFormatter,
  calculateRangeDate,
  startDateOfBucket,
  daysInBucket,
  makeTimeBucket,
  getWeekString,
  isBetween,
  nearDay,
  finishedNearDay,
  getDateWithoutTime,
  getToday,
  getMyDayId,
  toTimeString,
  getStartDayByRange,
  getWeekOfYear,
  isDateString
}

export default dateUtils;